import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  isSupported,
  getPermission,
  subscribe,
  getCurrentSubscription,
} from "../lib/pushNotifications";

const DISMISS_KEY = "push_softprompt_dismissed_at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function isDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * One-shot soft prompt explaining why we'd like to send push notifications.
 *
 * Visibility rules (all must be true):
 *   - Browser supports SW + Push + Notification
 *   - Notification.permission === "default" (user hasn't decided yet)
 *   - No active push subscription is registered
 *   - User hasn't dismissed the card in the last 14 days
 */
export default function PushSoftPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSupported()) return;
      if (getPermission() !== "default") return;
      if (isDismissed()) return;
      const sub = await getCurrentSubscription();
      if (sub) return; // already subscribed somehow
      if (!cancelled) setVisible(true);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!visible) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribe();
      toast.success("Notifications enabled", {
        description: "You'll get a nudge when an expense needs a comment.",
      });
      setVisible(false);
    } catch (e) {
      const msg = e?.message || "Couldn't enable notifications.";
      toast.error("Notifications blocked", { description: msg });
      // If the user actively denied, we won't be able to ask again — hide.
      if (getPermission() === "denied") setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  return (
    <Card
      className="mb-4 border-[#1e2a78]/15 bg-gradient-to-r from-[#1e2a78]/5 to-transparent"
      data-testid="push-softprompt">
      <CardContent className="flex flex-wrap items-center gap-3 py-3 pl-3 pr-2 sm:flex-nowrap">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1e2a78]/10 text-[#1e2a78]">
          <Bell className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">
            Get reminded when expenses are missing comments
          </p>
          <p className="text-xs text-slate-600">
            We'll only ping you once per pending expense, after 24h and again after 72h.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            onClick={enable}
            disabled={busy}
            className="bg-[#1e2a78] hover:bg-[#0F1E3D]"
            data-testid="push-softprompt-enable">
            Enable
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            disabled={busy}
            data-testid="push-softprompt-dismiss">
            Not now
          </Button>
          <button
            type="button"
            aria-label="Close"
            onClick={dismiss}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
